import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Subject, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DebounceService } from 'src/app/modules/shared/debounce.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { GridSearchItem } from '../../entities/grid-search-item.class';
import { Product } from '../../entities/products.class';
import { QuoteDetailsService } from '../../services/quote-details.service';
import { QuoteToolsService } from '../../services/quote-tools.service';

@Component({
  selector: 'app-grid-product-search',
  templateUrl: './grid-product-search.component.html',
  styleUrls: ['./grid-product-search.component.scss'],
  standalone: false
})
export class GridProductSearchComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() disabled = false;
  @Input() countryCodeId: number;
  @Input() verticalLabel = true;
  @Output() toolSelected = new EventEmitter<Product>();

  @ViewChild('productsSearchField') productsSearchField: ElementRef;
  @ViewChild('scrollViewPort') scrollViewPort: CdkVirtualScrollViewport;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  appStrings: AppLocalization;
  isLoading = false;
  isMobile: boolean;
  hasResults = false;
  search = new UntypedFormControl('');
  products: GridSearchItem[] = [];
  searchText = '';
  resultsCount = 0;
  resultsText = '';
  panelHeight = '192px';
  loadingMore = false;
  currentPage = 0;
  searchTerm$ = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(
    private appInfoService: AppInfoService,
    private debounceService: DebounceService,
    private quoteToolsService: QuoteToolsService,
    private quoteDetailsService: QuoteDetailsService,
    private deviceService: DeviceDetectorService,
    private ref: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();

    this.subscriptions.add(
      this.searchTerm$.pipe(
        switchMap(searchString => {
          const page = searchString ? 0 : ++this.currentPage;
          if (page) {
            this.loadingMore = true;
            this.ref.detectChanges();
          } else {
            this.loadingMore = false;
            this.products = [];
            this.isLoading = true;
            this.currentPage = 0;
            this.ref.detectChanges();
          }
          if (!this.searchText) {
            this.isLoading = false;
            return (of({ count: 0, items: [] }));
          }
          return this.quoteToolsService.getProducts(this.searchText, this.countryCodeId, page);
        })
      ).subscribe(result => {
        if (this.loadingMore) {
          this.products = [...this.products, ...result.items];
          this.loadingMore = false;
          // Force a change detection to display the new results
          this.ref.detectChanges();
        } else {
          this.isLoading = false;
          this.products = result.items;
          this.hasResults = Boolean(result.items.length);
          if (this.hasResults) {
            this.resultsText = this.appStrings.products_searchResults.replace('{0}', result.count.toString());
            this.resultsCount = result.count;
            if (result.items.length < 5) {
              this.panelHeight = `${result.items.length * 48}px`;
            } else {
              this.panelHeight = '192px';
            }
          }
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      if (changes.disabled.currentValue && !this.search.disabled) {
        this.search.disable();
      }

      if (!changes.disabled.currentValue && this.search.disabled) {
        this.search.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.scrollViewPort.elementScrolled().subscribe((event: any) => {
      const threshold = 0.95 * 100 * event.target.scrollHeight / 100;
      const current = event.target.scrollTop + event.target.clientHeight;

      if (current > threshold && !this.loadingMore) {
        this.loadMore();
      }
    });
  }

  /**
   * Handles typing into the autocomplete
   */
  searchAutocomplete() {
    const searchText = this.search.value as string;
    this.searchText = searchText;
    this.currentPage = 0;

    if (searchText) {
      if (this.products.length) {
        this.scrollViewPort.scrollToIndex(0);
      }
      this.isLoading = true;
      this.debounceService.debounceCall(1000, () => this.searchTerm$.next(searchText));
    } else {
      this.products = [];
      this.hasResults = false;
      this.resultsText = '';
    }
  }

  /**
   * Handles selecting a product from the autocomplete
   */
  productSelected($event: MatAutocompleteSelectedEvent) {
    this.search.setValue(this.searchText);

    const selectedProduct: GridSearchItem = $event.option.value;
    if (selectedProduct) {
      const product = new Product();
      product.productNumber = selectedProduct.itemCode;
      product.quantity = 1;

      this.toolSelected.emit(product);
      this.productsSearchField.nativeElement?.blur();
      if (this.deviceService.isMobile()) {
        this.search.setValue('');
      }
    }
  }

  /**
   * Triggers loading another batch of products if not all of them are loaded
   */
  loadMore() {
    if (this.products.length >= this.resultsCount) {
      return;
    }

    this.searchTerm$.next(undefined);
  }
}
